import { formatDate } from '@angular/common';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';

const MAX_SESSIONS_TO_DOWNLOAD = 30;
import { ActivatedRoute } from '@angular/router';
import { saveAs } from 'file-saver';
import { InfoCardComponent } from 'src/app/components/shared/info-card/info-card.component';
import { ApiSalesService } from 'src/app/services/api-sales.service';
import { EventosService } from 'src/app/services/eventos.service';
import { PanelAdminService } from 'src/app/services/panel-admin.service';
import { ReportsService } from 'src/app/services/reports.service';

@Component({
  selector: 'app-reports-event',
  templateUrl: './reports-event.component.html',
  styleUrls: ['./reports-event.component.scss']
})
export class ReportsEventComponent implements OnInit {

  idEvent!: any;
  event!: any;

  // Informe resumido. La asistencia viene dentro de esta misma respuesta, así
  // que ya no hace falta la llamada aparte ni su tabla de Material.
  summary!: any;
  loadedSummary: boolean = false;
  errorSummary: boolean = false;
  // Secciones plegables de la pestaña
  summaryCollapsed: boolean = false;
  advancedCollapsed: boolean = false;
  @ViewChild('summaryTitle') summaryTitle!: ElementRef<HTMLElement>;
  // Altura a la que se ancla el botón de descarga: la de la fila del título
  downloadButtonTop: number = 140;
  downloadingPdf: boolean = false;

  business!: any;

  // Sessions
  sessions!: any[];
  filteredSessions!: any[];
  selectedSession!: any[];

  // Fechas
  datesRange!: any;
  stringDate: string = '';
  openDatePicker: boolean = false;
  dateFilter!: any;

  // Metodos
  methodsOptions!: any;
  methodSelected: any[] = [];
  allMethods: boolean = true;

  // Tipo de informe
  typeReport: any = 'global';
  formatReport: any = 'pdf';

  // Header Excel
  reportsGroupEnabled!: any;
  reportsGroupSelected: any[] = [];
  allColumns: boolean = false;

  // Cargar informe
  loadingReport: boolean = false;

  constructor(private activatedRoute: ActivatedRoute, private panelAdminService: PanelAdminService, private apiSalesService: ApiSalesService,
    private dialog: MatDialog, private eventService: EventosService, private reportsService: ReportsService) { }

  ngOnInit(): void {
    this.apiSalesService.getAllMethods().subscribe({
      next: (res) => {
        this.methodsOptions = res.data;
        this.selectAllMethods();
      }
    });

    this.reportsService.getHeaderReports().subscribe({
      next: (res) => {
        this.reportsGroupEnabled = res;
        this.reportsGroupSelected = this.reportsGroupEnabled.map(value => value.id);
      }
    })
  }

  ngAfterViewInit(): void {
    this.business = JSON.parse(localStorage.getItem('business') || '{}');
    this.activatedRoute.params.subscribe({
      next: (params) => {
        this.idEvent = +params.idEvent;
        this.eventService.getEventById(this.idEvent).subscribe({
          next: (res: any) => {
            this.event = res.data;
            this.sessions = res.data.sessions;
            this.filteredSessions = res.data.sessions; // Inicializar con todas las sesiones
            // Ordenar sesiones
            this.filteredSessions.sort((a: any, b: any) => {
              let dateA = new Date(a.start_date);
              let dateB = new Date(b.start_date);
              return dateB.getTime() - dateA.getTime();
            });
            this.loadSummary();
          }
        });
      }
    });
  }

  loadSummary() {
    this.loadedSummary = false;
    this.errorSummary = false;
    this.eventService.getSalesSummaryByEvent(this.idEvent).subscribe({
      next: (res: any) => {
        this.summary = res.data;
        this.loadedSummary = true;
        this.alignDownloadButton();
      }, error: () => {
        this.errorSummary = true;
        this.loadedSummary = true;
      }
    });
  }

  /**
   * Descarga el informe resumido en PDF, maquetado por la API.
   *
   * Sustituye al diálogo de impresión del navegador: cada uno se comportaba de
   * una forma y en Safari guardar en PDF estaba escondido en un desplegable.
   */
  downloadSummaryPdf() {
    if (this.downloadingPdf) {
      return;
    }
    this.downloadingPdf = true;

    this.eventService.downloadSalesSummaryPdf(this.idEvent).subscribe({
      next: (file: any) => {
        // El nombre lo pone la API en la cabecera, pero al venir por XHR no
        // llega al navegador: se reconstruye aquí.
        const name = (this.event?.title || 'evento')
          .toLowerCase()
          .normalize('NFD').replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        saveAs(file, `informe-resumido-${name}.pdf`);
        this.downloadingPdf = false;
      },
      error: () => {
        this.infoDialog('¡Vaya! No se ha podido generar el informe en PDF. Vuelve a intentarlo más tarde.');
        this.downloadingPdf = false;
      }
    });
  }

  /**
   * Ancla el botón de descarga a la altura del título del informe.
   *
   * Se mide en vez de ponerlo a ojo porque lo que hay por encima (cabecero del
   * panel, migas, el enlace de volver) cambia de alto según la resolución y el
   * zoom, y un valor fijo lo dejaba flotando sobre la cabecera.
   */
  private alignDownloadButton() {
    setTimeout(() => {
      const row = this.summaryTitle?.nativeElement;
      if (!row) {
        return;
      }
      // getBoundingClientRect da la posición en pantalla, que es contra lo que
      // se ancla un `sticky`; se le suma el scroll por si se entra a la
      // pestaña con la página ya bajada.
      const top = row.getBoundingClientRect().top + window.scrollY;
      // Nunca por encima del cabecero del panel, que se ancla a 76px
      this.downloadButtonTop = Math.max(top, 84);
    }, 0);
  }


  /**
   * Porcentaje de entradas escaneadas sobre las vendidas de una sesión. Con 0
   * vendidas devuelve un guion: un 0% ahí daría a entender que no fue nadie,
   * cuando lo que pasa es que no había nada que leer.
   */
  attendanceRate(sold: number, readed: number): string {
    if (!sold) {
      return '—';
    }
    const rate = readed * 100 / sold;
    // Coma y no punto: `toFixed` usa el separador inglés y dejaba un "69.8%"
    // en medio de un informe donde todo lo demás va en formato español.
    return `${rate.toFixed(rate % 1 === 0 ? 0 : 1).replace('.', ',')}%`;
  }

  // Color fijo por canal (no por posición) para que el mismo canal se vea
  // siempre igual al saltar de un evento a otro. Son tonos de la paleta del
  // panel; lo que no es un canal de venta (abonados, pedidos sin canal) va gris.
  private readonly channelColors: { [id: number]: string } = {
    1: '#FF4D80', // Online
    2: '#13CDE2', // TPV
    3: '#2DAE29', // Efectivo
    4: '#FF763A', // Totem
    5: '#7A5AF8', // App
  };

  channelColor(row: any): string {
    return this.channelColors[row?.id_channel] || '#AAAAAA';
  }

  // El canal viene como id crudo de orders.id_channel: el nombre sale de los
  // canales de venta que ya carga el filtro del informe avanzado.
  channelName(row: any): string {
    if (row?.id_channel == null) {
      return 'Sin especificar';
    }
    const channel = this.methodsOptions?.find(method => method.id == row.id_channel);
    return channel?.name || `Canal ${row.id_channel}`;
  }

  // Lo vendido sin butaca en un evento con mapa llega con la zona a null: son
  // ventas antiguas, de antes de que se guardara siempre la butaca.
  levelName(row: any): string {
    return row?.name || 'Sin zona asignada';
  }

  // Seleccionar todas las opciones
  selectAllMethods() {
    this.methodSelected = this.methodsOptions?.map(option => option.id);
  }

  changeTypeReport() {
    if (this.typeReport == 'detail' && this.formatReport == 'pdf') {
      this.formatReport = 'csv';
      this.allColumns = true;
      this.selectAllColumns();
    }
  }

  selectAllColumns() {
    if (this.allColumns) {
      this.reportsGroupSelected = this.reportsGroupEnabled.map(value => value.id);
      this.reportsGroupEnabled.forEach(element => {
        element.selected = true;
      });
    }
  }

  selectColumn(column, i) {
    let index = this.reportsGroupSelected.findIndex(value => value == column.id);
    index == -1 ? this.reportsGroupSelected.push(column.id) : this.reportsGroupSelected.splice(index, 1);
    if (this.reportsGroupEnabled.length != this.reportsGroupSelected.length) {
      this.allColumns = false;
    }
  }

  // Seleccionar Rango de fechas
  selectDates(datesRange: any) {
    this.datesRange = datesRange;
    let stringDates = this.formatStringDate(datesRange.min);
    if (datesRange.max) {
      stringDates += ' - ' + this.formatStringDate(datesRange.max);
    }
    this.stringDate = stringDates;
    this.dateFilter = { date_from: this.formatStringDate(datesRange.min, 'dd/MM/yyyy'), date_to: this.formatStringDate(datesRange.max, 'dd/MM/yyyy') };

    // Filtrar sesiones según el rango de fechas
    this.filterSessionsByDateRange();
  }

  filterSessionsByDateRange() {
    if (!this.dateFilter || !this.sessions) {
      this.filteredSessions = this.sessions;
      return;
    }

    const fromDate = new Date(this.formatStringDate(this.datesRange.min, 'yyyy-MM-dd'));
    const toDate = new Date(this.formatStringDate(this.datesRange.max, 'yyyy-MM-dd'));

    // Ajustar toDate al final del día
    toDate.setHours(23, 59, 59, 999);

    this.filteredSessions = this.sessions.filter(session => {
      const sessionDate = new Date(session.start_date);
      return sessionDate >= fromDate && sessionDate <= toDate;
    });

    // Limpiar selección si alguna sesión seleccionada ya no está en el filtro
    if (this.selectedSession && this.selectedSession.length > 0) {
      this.selectedSession = this.selectedSession.filter(selectedId =>
        this.filteredSessions.some(session => session.id === selectedId)
      );
    }
  }

  formatStringDate(date: any, format: string = 'dd MMM yy') {
    let dateFormat = null;
    if (date) {
      dateFormat = formatDate(new Date(date), format, 'es', '+0000');
    }
    return dateFormat;
  }

  clearRangeDates(event: any) {
    if (event) {
      event.stopPropagation();  // Evita que el click se propague al div
    }
    this.datesRange = null;
    this.stringDate = '';
    this.dateFilter = null;
    this.openDatePicker = false;

    // Restaurar todas las sesiones
    this.filteredSessions = this.sessions;
  }

  downloadPdf() {
    const totalSessions =
      this.selectedSession && this.selectedSession.length > 0
        ? this.selectedSession.length
        : this.sessions?.length || 0;

    if (totalSessions >= MAX_SESSIONS_TO_DOWNLOAD) {
      this.infoDialog(
        `No se pueden descargar ${totalSessions} elementos a la vez, por favor, prueba a descargar informes mensuales o seleccionar un máximo de ${MAX_SESSIONS_TO_DOWNLOAD} eventos o sesiones para completar la descarga.`,
      );
      return;
    }

    this.loadingReport = true;
    const methods = this.methodSelected?.length != this.methodsOptions?.length ? this.methodSelected : null;
    const reports = this.reportsGroupSelected?.length != this.reportsGroupEnabled?.length ? this.reportsGroupSelected : null;

    const eventPayload: any = {id: this.event.id};
    // Solo incluir sesiones si se seleccionaron explícitamente
    if(this.selectedSession && this.selectedSession.length > 0) {
      eventPayload.sessions = this.selectedSession;
    }else if(this.sessions && this.sessions.length > 0){
      // Si no se seleccionó ninguna sesión pero hay sesiones disponibles, incluir todas las sesiones
      eventPayload.sessions = this.sessions.map(session => session.id);
    }

    let payload: any = {
      events: [eventPayload],
      dates: this.dateFilter,
      format: this.formatReport,
      type: this.typeReport,
      method: methods,
      reportsgroup: reports
    };

    this.reportsService.downloadReportFile(this.business?.id, payload).subscribe({
      next: (res) => {
        if (res) {
          const linkSource = res;
          const type = this.formatReport == 'csv' ? 'text/csv' : 'application/pdf';
          const base64WithMime = `data:${type};base64,${linkSource}`;

          const newTab = window.open('', '_blank');
          if (newTab) {
            newTab.document.body.innerHTML = `
              <iframe src="${base64WithMime}" frameborder="0" style="border:0; width:100%; height:100%;" allowfullscreen></iframe>
            `;
            newTab.document.title = 'Informe';
          }
        }
        this.loadingReport = false;
      }, error: (err) => {
        this.infoDialog('¡Vaya! Algo ha fallado, no se ha podido descargar el informe. Vuelve a intentarlo más tarde.');
        this.loadingReport = false;
      }
    })
  }

  infoDialog(msg: string, icon: boolean = true) {
    const dialogRef = this.dialog.open(InfoCardComponent, {
      panelClass: 'info-dialog',
      data: {
        icon: icon,
        text: msg,
        btnCancel: false
      }
    });
  }

}
